import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import ContentNotification from '@/components/ContentNotfication';
import LargeCard from '@/components/LargeCard';
import LoadingIndicator from '@/components/LoadingIndicator';
import QuickSearch from '@/components/QuickSearch';
import PERMISSIONS from '@/constants/auth.constants';
import { CHANGE_REQUEST_INTERNAL_NETWORKS_TEMPLATE } from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import useConfirm from '@/hooks/useConfirm';
import useHandleError from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  EMPTY_TICKET_TEMPLATE,
  ticketDialogIsOpenAtom,
  ticketDialogTemplateAtom
} from '@/pages/Ticket';
import {
  axiosClient,
  getResponseErrorMessages
} from '@/services/BaseApi.service';
import { isLayoutMobileAtom } from '@/store/layout';
import { userAtom } from '@/store/user.store';

import { useFetchSettings } from '../Administration.store';
import useStyles from './AdministrationEnvironments.styles';
import {
  sortEnvironments,
  validatedEnvironments
} from './AdministrationEnvironments.utils';

const AdministrationEnvironments = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [, setTicketDialogIsOpen] = useAtom(ticketDialogIsOpenAtom);
  const [, setTicketDialogTemplate] = useAtom(ticketDialogTemplateAtom);
  const [user] = useAtom(userAtom);
  const { refetch, data: orgSettings, isLoading } = useFetchSettings();
  const [currentEnvironments, setCurrentEnvironments] = useState<string[]>();
  const [deletedEnvironments, setDeletedEnvironments] = useState<number>();
  const ipInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasErrors, setHasErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { classes } = useStyles();

  const handleError = useHandleError() as (
    error: AxiosError<unknown>,
    message: string
  ) => void;
  const { handleSubmit } = useForm();
  const { ConfirmationModal: IPDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Edit Network?',
      message: `Please confirm that you want to delete ${deletedEnvironments} address${
        deletedEnvironments !== 1 ? 'es' : ''
      } with your edits.`,
      button: 'Delete'
    });

  useEffect(() => {
    if (orgSettings?.environments) {
      const environments = Array.isArray(orgSettings.environments)
        ? orgSettings.environments
        : [];
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentEnvironments(sortEnvironments(environments));
    } else {
      setCurrentEnvironments([]);
    }
  }, [orgSettings]);

  const handleChangeRequest = () => {
    setTicketDialogIsOpen(true);
    setTicketDialogTemplate({
      ...EMPTY_TICKET_TEMPLATE,
      ...CHANGE_REQUEST_INTERNAL_NETWORKS_TEMPLATE
    });
  };

  const handleRemoveIP = async (ip: string) => {
    const newEnvironments = currentEnvironments?.filter(
      (address) => address !== ip
    );
    setDeletedEnvironments((deletedEnvironments || 0) + 1);
    setCurrentEnvironments(newEnvironments);
  };

  const handleSearchInput = useMemo(
    () => debounce((term: string) => setSearchQuery(term), 300),
    []
  );

  const setInputValue = (value: string) => {
    if (ipInputRef.current) {
      ipInputRef.current.value = value;
    }
  };

  const handleInput = (value: string) => {
    setInputValue(value);
    setHasErrors(false);
    setErrorMessage('');
  };

  const onSubmit = async () => {
    const confirmed = deletedEnvironments ? await confirmDelete() : true;
    if (confirmed) {
      const initialInput = ipInputRef.current?.value || '';
      const { validIPs, invalidIPs } = validatedEnvironments(
        ipInputRef.current?.value || '',
        currentEnvironments
      );

      setHasErrors(false);
      setErrorMessage('');

      if (invalidIPs.length > 0) {
        setHasErrors(true);
        setErrorMessage(
          'The remaining IP Addresses contain errors.  Please fix and resubmit.'
        );
        setInputValue(invalidIPs.join('\n'));
      }

      try {
        if (validIPs.length > 0 || deletedEnvironments) {
          const environments = [...(currentEnvironments || []), ...validIPs];
          const response = await axiosClient.put(
            `organization_settings/${selectedOrganization.label}`,
            { ...orgSettings, environments: JSON.stringify(environments) }
          );

          setCurrentEnvironments(sortEnvironments(environments));
          setInputValue(invalidIPs.join('\n'));
          setDeletedEnvironments(undefined);
          if (response.status === 200) {
            enqueueSnackbar('Organization Information Updated', {
              variant: 'success'
            });
          } else {
            enqueueSnackbar('Failed to save network', {
              variant: 'error'
            });
          }

          refetch();

          return response;
        }
      } catch (error) {
        handleError(
          error as AxiosError<unknown>,
          getResponseErrorMessages(error as AxiosError<unknown>)
        );
        setInputValue(initialInput);
        return null;
      }
    }
    return null;
  };

  const filteredIPAddresses = useMemo(() => {
    return (
      currentEnvironments?.filter((ip) => {
        return ip.includes(searchQuery);
      }) || []
    );
  }, [currentEnvironments, searchQuery]);

  if (isLoading || currentEnvironments === undefined)
    return <LoadingIndicator isLoading />;
  return (
    <>
      {selectedOrganization.label === 'all' && (
        <ContentNotification
          title="Select an organization"
          text="Please select an organization to view and edit network."
        />
      )}
      <LargeCard title="Internal Network">
        <Box sx={{ mb: 2 }}>
          The networks listed here represent the public scope IP address spaces
          your organization uses for internal networks, DMZ, cloud
          infrastructure, and other digital assets you own or manage. Providing
          this information helps Pondurance analysts better understand your
          environment, allowing us to deliver more accurate monitoring and
          threat detection. For customers utilizing our NDR service, these
          network settings will be automatically applied to your network
          sensors. RFC 1918 and other private scope IP addresses (
          <i>
            10.0.0.0/8, 192.168.0.0/16, 172.16.0.0/12, 169.254.0.0/16,
            127.0.0.0/8, 0.0.0.0/8, 255.255.255.255/32
          </i>
          ) are automatically included and won&apos;t be listed here.
          <CheckPermissions permissions={PERMISSIONS.NOT_INTERNAL}>
            <p>
              If you would like to make changes, please create a ticket or click
              the &quot;Request Changes&quot; button.
            </p>
          </CheckPermissions>
        </Box>
        <Stack spacing={2.5}>
          <Grid container>
            {/* Left Column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack
                spacing={2}
                sx={{
                  marginBottom: isLayoutMobile && user?.internal ? '2rem' : 0,
                  paddingRight: isLayoutMobile ? 0 : '1.5rem',
                  overflow: 'hidden'
                }}
              >
                <Typography variant="h6">Current IP Addresses</Typography>
                <Box className={classes.ipList}>
                  <QuickSearch onChange={handleSearchInput} border />
                  <List dense sx={{ maxHeight: '100%', overflowY: 'auto' }}>
                    {filteredIPAddresses.map((ip) => (
                      <ListItem
                        key={ip}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            sx={{ py: 0, fontSize: '1rem' }}
                            onClick={() => handleRemoveIP(ip)}
                          >
                            {user?.internal && (
                              <FontAwesomeIcon icon={faCircleXmark} />
                            )}
                          </IconButton>
                        }
                      >
                        {ip}
                      </ListItem>
                    ))}
                  </List>
                </Box>
                {deletedEnvironments && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'red', marginTop: '0.25rem !important' }}
                    component="span"
                  >
                    {`${deletedEnvironments} address${
                      deletedEnvironments > 1 ? 'es' : ''
                    } will be deleted.`}
                  </Typography>
                )}
              </Stack>
            </Grid>

            {/* Right Column */}
            {user?.internal && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Add New IP Addresses</Typography>
                  <TextField
                    className={classes.ipInput}
                    variant="outlined"
                    multiline
                    inputRef={ipInputRef}
                    onChange={(e) => handleInput(e.target.value)}
                    placeholder="Enter IP addresses here, one per line."
                    fullWidth
                    error={hasErrors}
                    helperText={
                      hasErrors
                        ? errorMessage
                        : 'Enter one entry per line. Supports single IP address, address range, or CIDR.'
                    }
                    sx={{ borderColor: hasErrors ? 'red' : 'inherit' }}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>

          <IPDeleteModal />
          {!user?.internal && (
            <Box sx={{ ml: 'auto' }}>
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={handleChangeRequest}
              >
                Request Changes
              </Button>
            </Box>
          )}
          {user?.internal && (
            <Box
              sx={{ ml: 'auto', display: 'flex', justifyContent: 'flex-end' }}
            >
              <form onSubmit={handleSubmit(onSubmit)}>
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  type="submit"
                >
                  Save
                </Button>
              </form>
            </Box>
          )}
        </Stack>
      </LargeCard>
    </>
  );
};

export default AdministrationEnvironments;
